import React, { useEffect, useRef, useState } from 'react';
import { Price } from '../../../../../UI/Price/Price';
import Swiper from '../../../../../Swiper/Swiper';
import { SwiperSlide } from 'swiper/react';
import { getSwatchIndices, getSwiperSettings, sortedSwatchGroups } from './utils';

function SwatchItem({ selected, item, attrId, disabled, onClick, overflow }) {
  const value = item.colorForSwatch;
  return (
    <>
      <span
        className={`swatches__span ${disabled ? 'swatches__span--notAvailable' : 'swatches__span--available'}`}
        data-attrval={value}
        data-attrid={attrId}
        data-attrtype='Swatch'
        onClick={onClick}
      />
      <button
        className={`btn-tertiary p-0 ${overflow ? 'overflowSwatches' : ''}`}
        onClick={onClick}
        data-attrval={value}
        data-attrid={attrId}
        data-attrtype='Swatch'
        aria-current={selected}
        aria-disabled={disabled}
        type='button'
        data-role='none'
        disabled={disabled}
      >
        <img
          className={`swatches__img ${selected ? 'swatches__img--selected' : ''}`}
          src={`//dimg.dillards.com/is/image/DillardsZoom/${item.swatchImg}`}
          alt={`Color Swatch - ${value}`}
          onClick={onClick}
          data-attrval={value}
          data-attrid={attrId}
          data-attrtype='Swatch'
          aria-current={selected}
        />
        <span className='sr-only'>Selecting a color swatch updates the main product image.</span>
      </button>
    </>
  );
}

export default function ProductDetailSwatches(props) {
  const swatchGroups = sortedSwatchGroups(props.swatchGroups || []);
  const [seeAllSwatches, setSeeAllSwatches] = useState([]);
  const swiperRefs = useRef({});

  const { selectedMainImg, currentAttributeState } = props.passedFunctions.parentState;

  const { siteFormat, quickviewIsOpen } = props.passedAppFunctions;

  const isQuickView =
    siteFormat === 'desktop' && props.contentData.productLayout === 'QuickViewDisplay' && quickviewIsOpen;

  useEffect(() => {
    if (!selectedMainImg) return;

    const indices = getSwatchIndices(selectedMainImg, swatchGroups);
    if (!indices) return;

    const slider = swiperRefs.current[isQuickView ? '0' : indices.swatchGroupIndex];
    if (!slider) return;

    if (isQuickView) {
      slider.slideTo(indices.realIndex);
    } else {
      slider.slideTo(indices.swatchIndex);
    }
  }, [selectedMainImg, swatchGroups, seeAllSwatches]);

  const handleSeeAllClick = (index) => (e) => {
    e.preventDefault();
    setSeeAllSwatches((prevSeeAllSwatches) => {
      const newSeeAllSwatches = [...prevSeeAllSwatches];
      newSeeAllSwatches[index] = !newSeeAllSwatches[index];
      return newSeeAllSwatches;
    });
  };

  const isAvailable = (attrVal) => {
    return props.availableValues.includes(attrVal);
  };

  const findAttrId = (attrName) => {
    const attrObj = props.contentData.skus[0].definingAttributes.find((a) => a.name === attrName);
    return attrObj?.id;
  };

  if (swatchGroups.length === 0) return;

  if (isQuickView) {
    return (
      <div className='productDisplay__div--swatch-group modalSwatches'>
        <Swiper
          onSwiper={(ref) => {
            swiperRefs.current['0'] = ref;
          }}
          slidesPerView={Object.keys(props.contentData.extraQVProps).length === 0 ? 6 : 4}
          navigation
          watchOverflow
          speed={500}
          a11y={{
            containerRole: 'list',
          }}
        >
          {swatchGroups.map((swatchGroup, index) =>
            swatchGroup.swatches.map((swatch) => {
              const disabled = !isAvailable(swatch.colorForSwatch);
              return (
                <SwiperSlide className={'swatches__item'} key={swatch.swatchImg}>
                  <SwatchItem
                    item={swatch}
                    attrId={findAttrId('Color')}
                    disabled={disabled}
                    selected={currentAttributeState['Color'].selectedValue === swatch.colorForSwatch}
                    onClick={disabled ? null : props.changeAttributeState}
                  />
                </SwiperSlide>
              );
            })
          )}
        </Swiper>
      </div>
    );
  }

  return (
    <div className='swatch-groups'>
      {swatchGroups.map((swatchGroup, index) => {
        return (
          <div key={index} className='productDisplay__div--swatch-group'>
            <Swiper
              containerClass={seeAllSwatches[index] ? 'hidden' : ''}
              onSwiper={(swiperRef) => {
                swiperRefs.current[`${index}`] = swiperRef;
              }}
              a11y={{
                containerRole: 'list',
              }}
              {...getSwiperSettings(siteFormat)}
            >
              {swatchGroup.swatches.map((swatch) => {
                const disabled = !isAvailable(swatch.colorForSwatch);
                return (
                  <SwiperSlide className={'swatches__item'} key={swatch.swatchImg}>
                    <SwatchItem
                      item={swatch}
                      attrId={findAttrId('Color')}
                      disabled={disabled}
                      selected={currentAttributeState['Color'].selectedValue === swatch.colorForSwatch}
                      onClick={disabled ? null : props.changeAttributeState}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            {seeAllSwatches[index] && (
              <ul className={'swatches'}>
                {swatchGroup.swatches.map((swatch) => {
                  const disabled = !isAvailable(swatch.colorForSwatch);
                  return (
                    <li key={swatch.swatchImg} className='swatches__item'>
                      <SwatchItem
                        item={swatch}
                        attrId={findAttrId('Color')}
                        disabled={disabled}
                        onClick={disabled ? null : props.changeAttributeState}
                        selected={currentAttributeState['Color'].selectedValue === swatch.colorForSwatch}
                        overflow
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            <div className='row mx-0 mt-1 align-items-start'>
              {swatchGroups.length > 1 || props.contentData.onSpecial ? <Price pricing={swatchGroup.pricing} /> : ''}
              {swatchGroup.swatches.length > 6 && (
                <div
                  className={
                    swatchGroup.swatches.length < 8 && siteFormat === 'mobile'
                      ? 'seeAllSwatchesWrapperHide'
                      : swatchGroup.swatches.length <= 10 && siteFormat === 'mobile'
                        ? 'seeAllSwatchesWrapperHideLandscape'
                        : 'seeAllSwatchesWrapper'
                  }
                >
                  <button id='seeAllSwatches' className='btn p-0' onClick={handleSeeAllClick(index)}>
                    {seeAllSwatches[index] ? 'See fewer swatches' : 'See all swatches'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
